import { Close, Icon } from "@shared/ui";
import { createPortal } from "react-dom";

import ButtonPrimary from "./ButtonPrimary";
import ButtonSecondary from "./ButtonSecondary";

/**
 * Modal for confirming user enable/disable
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Function to close the modal
 * @param {Object} member - The team member being enabled/disabled
 * @param {Function} onConfirm - Function to call when confirming the action
 */
const DeleteUserModal = ({ isOpen, onClose, member, onConfirm }) => {
  const handleConfirm = (e) => {
    e.preventDefault();
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  if (!isOpen || !member) return null;

  const isDeactivated = member.userStatus?.toLowerCase() === "deactivated";
  const isEnable = isDeactivated;
  const actionText = isEnable ? "Enable" : "Disable";
  const actionTextLower = isEnable ? "enable" : "disable";

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2
            className={`text-xl font-semibold ${
              isEnable ? "text-green-600" : "text-red-600"
            }`}
          >
            {actionText} User
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 focus:outline-none"
            aria-label="Close"
          >
            <Icon icon={Close} className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <p className="text-sm text-gray-700 mb-4">
              Are you sure you want to {actionTextLower}{" "}
              <span className="font-medium">{member.name || member.email}</span>
              {isEnable
                ? "? This will restore their access to the dashboard."
                : "? This will remove their access to the dashboard."}
            </p>
            {!isEnable && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">
                  <span className="font-medium">Warning:</span> This will
                  disable the user, and they will lose access to the dashboard.
                  You can re-enable them later.
                </p>
              </div>
            )}
            {isEnable && (
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <p className="text-sm text-green-800">
                  <span className="font-medium">Note:</span> This will restore
                  the user's access to the dashboard.
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-x-3">
            <ButtonSecondary text="Cancel" onClick={onClose} type="button" />
            <ButtonPrimary
              text={`${actionText} User`}
              onClick={handleConfirm}
              type="button"
              className={
                isEnable
                  ? "bg-green-600 hover:bg-green-700 border-green-600"
                  : "bg-red-600 hover:bg-red-700 border-red-600"
              }
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeleteUserModal;
